import React, { useState } from 'react';
import type { HistorySnapshots } from '../hooks/useHistorySnapshots';

/**
 * HistoryBar - the Save Design / Undo / Redo control strip shared by
 * every design editor (owner's requirement, 2026-10-26). When the
 * editor opted into account saving and the user is signed in, Save
 * Design also writes the design to their account, and a My Designs
 * picker lists, loads and deletes the account copies.
 */
export default function HistoryBar({ history, dark, label }: { history: HistorySnapshots; dark?: boolean; label?: string }) {
    const {
        undo, redo, save, canUndo, canRedo, dirty, savedAt, saveNote,
        accountEnabled, accountDesigns, accountId, accountBusy,
        saveToAccount, loadAccountDesign, deleteAccountDesign
    } = history;
    const [picked, setPicked] = useState<string>('');

    const btn = (enabled: boolean): React.CSSProperties => ({
        padding: '7px 14px',
        borderRadius: 8,
        border: `1px solid ${dark ? 'rgba(255,255,255,0.18)' : '#CBD5E1'}`,
        background: enabled
            ? (dark ? 'rgba(255,255,255,0.10)' : '#FFFFFF')
            : (dark ? 'rgba(255,255,255,0.03)' : '#F1F5F9'),
        color: enabled ? (dark ? '#F1F5F9' : '#0F172A') : (dark ? '#64748B' : '#94A3B8'),
        fontSize: 12,
        fontWeight: 600,
        cursor: enabled ? 'pointer' : 'not-allowed',
        whiteSpace: 'nowrap' as const
    });

    const status = saveNote
        ? saveNote
        : dirty
            ? 'Unsaved changes'
            : savedAt
                ? `Saved ${new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                : 'Not saved yet';

    const onSave = () => {
        save();
        // saveToAccount no-ops for guests and editors without account
        // saving; calling it unconditionally covers users who signed
        // in after the editor mounted.
        saveToAccount();
    };

    const selectedId = picked || accountId || '';

    return (
        <div
            data-history-bar
            style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                padding: '8px 10px', borderRadius: 10,
                background: dark ? 'rgba(20,22,29,0.92)' : '#F8FAFC',
                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#E2E8F0'}`
            }}
        >
            {label && (
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: dark ? '#94A3B8' : '#64748B', marginRight: 2 }}>
                    {label}
                </span>
            )}
            <button type="button" onClick={undo} disabled={!canUndo} style={btn(canUndo)} title="Undo the last design change (Ctrl+Z)">
                ↩ Undo
            </button>
            <button type="button" onClick={redo} disabled={!canRedo} style={btn(canRedo)} title="Redo the change you undid (Ctrl+Shift+Z)">
                ↪ Redo
            </button>
            <button
                type="button"
                onClick={onSave}
                disabled={accountBusy}
                style={{
                    ...btn(true),
                    background: dark ? '#2563EB' : '#2563EB',
                    border: '1px solid #2563EB',
                    color: '#FFFFFF'
                }}
                title={accountEnabled
                    ? 'Save this design to your account — it will be here on any device you sign in on'
                    : 'Save this design — it will still be here when you come back, until you undo it'}
            >
                💾 Save Design
            </button>
            {accountEnabled && (
                <>
                    <select
                        value={picked}
                        disabled={accountBusy}
                        onChange={(e) => {
                            const id = e.target.value;
                            if (!id) return;
                            setPicked(id);
                            // Reset to the placeholder once the load is
                            // under way: a native select fires no change
                            // event when its already-selected option is
                            // chosen again, which would make re-loading
                            // the current design a silent no-op.
                            loadAccountDesign(id).then(() => setPicked(''));
                        }}
                        title="Designs saved to your account"
                        style={{
                            padding: '7px 8px', borderRadius: 8, fontSize: 12, maxWidth: 230,
                            border: `1px solid ${dark ? 'rgba(255,255,255,0.18)' : '#CBD5E1'}`,
                            background: dark ? 'rgba(255,255,255,0.10)' : '#FFFFFF',
                            color: dark ? '#F1F5F9' : '#0F172A'
                        }}
                    >
                        <option value="">☁ My Designs ({accountDesigns.length})</option>
                        {accountDesigns.map(d => (
                            <option key={d.id} value={d.id}>
                                {d.name} — {new Date(d.updated_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            </option>
                        ))}
                    </select>
                    {selectedId && (
                        <button
                            type="button"
                            disabled={accountBusy}
                            onClick={() => { deleteAccountDesign(selectedId); setPicked(''); }}
                            style={btn(!accountBusy)}
                            title="Delete the selected design from your account"
                        >
                            🗑
                        </button>
                    )}
                </>
            )}
            <span style={{ fontSize: 11, color: dirty ? (dark ? '#FBBF24' : '#B45309') : (dark ? '#34D399' : '#047857') }}>
                {status}
            </span>
        </div>
    );
}
